import { useMemo } from 'react';

export type Option = {
  label: string;
  value: string;
};

type Tag = { name: string };

export interface Clinic {
  tenancy_id: string | number;
  clinic: string;
  clinic_id: string | number;
  primaryTeams?: string[];
  healthProfessionals?: string[];
  tags?: Tag[] | string[];
}

export type InsightCategory = Record<string, string | string[]>;

interface UseFilterOptionsParams {
  allTenancyClinics?: Clinic[];
  selectedTenancy?: { tenancy_id?: string | number } | null;
  insightCategory?: InsightCategory;
}

interface FilterOptionsResult {
  clinicOptions: Option[];
  ptOptions: Option[];
  hpOptions: Option[];
  tagOptions: Option[];
  esOptions: Option[];
  fcOptions: Option[];
}

export function useFilterOptions({
  allTenancyClinics,
  selectedTenancy,
  insightCategory,
}: UseFilterOptionsParams): FilterOptionsResult {
  return useMemo(() => {
    const clinics: Clinic[] = (allTenancyClinics || []).filter(
      (clinic) => clinic.tenancy_id === selectedTenancy?.tenancy_id,
    );

    const clinicOptions: Option[] = clinics
      .map((clinic) => ({
        label: clinic.clinic,
        value: String(clinic.clinic_id),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    const ptOptions: Option[] = clinics
      .flatMap((clinic) =>
        Array.isArray(clinic.primaryTeams) ? clinic.primaryTeams : [],
      )
      .filter((team, index, self) => self.indexOf(team) === index)
      .map((team) => ({ label: team, value: team }))
      .sort((a, b) => a.value.localeCompare(b.value));

    const hpOptions: Option[] = clinics
      .flatMap((clinic) =>
        Array.isArray(clinic.healthProfessionals)
          ? clinic.healthProfessionals
          : [],
      )
      .filter((hp, index, self) => self.indexOf(hp) === index)
      .map((hp) => ({ label: hp, value: hp }))
      .sort((a, b) => a.value.localeCompare(b.value));

    const tagStrings: string[] = clinics
      .filter((clinic) => (clinic.tags || []).length > 0)
      .flatMap((clinic) => {
        const tags = clinic.tags || [];
        if (tags.length === 0) return [] as string[];
        if (typeof tags[0] === 'string') {
          return tags as string[];
        }
        return (tags as Tag[]).map((t) => t.name);
      });

    const uniqueTagStrings: string[] = Array.from(new Set(tagStrings));

    const tagOptions: Option[] = uniqueTagStrings
      .map((name) => ({ label: name, value: name }))
      .sort((a, b) => a.value.localeCompare(b.value));

    const esOptions: Option[] = [
      { label: 'Open', value: 'open' },
      { label: 'Closed', value: 'closed' },
    ];

    const entries = Object.entries(insightCategory || {});
    const fcOptions: Option[] = entries
      .flatMap(([key, value]) => {
        if (Array.isArray(value)) {
          return value.map((v, index) => ({
            label: v,
            value: String(v),
          }));
        }
        return [{ label: String(value), value: String(value) }];
      })
      .sort((a, b) => a.value.localeCompare(b.value));

    return {
      clinicOptions,
      ptOptions,
      hpOptions,
      tagOptions,
      esOptions,
      fcOptions,
    };
  }, [allTenancyClinics, selectedTenancy, insightCategory]);
}
